import { useEffect, useRef, useState } from "react";
import { REIButton, REIDialoge, REIDivider, REIToastContainer } from "../../../components/ui";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import { Icons, MEMBER_KEY, Messages } from "../../../utils/constants";
import { httpDelete, httpPost, useHttpGets } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import AddContactDetails from "./AddContactDetails";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Tooltip } from "primereact/tooltip";

const ContactDetails: React.FC<{
  propertyTrackerId: number;
  properties: any[] | undefined;
}> = ({ propertyTrackerId, properties }) => {
  const memberId = localStorage.getItem(MEMBER_KEY);
  const [memberDetails, setMemberDetails] = useState<IMemberDetailsResponse>();
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const [contacts, setContacts] = useState<IContactDetailInfo[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
  const [reload, setReload] = useState(false);
  const [value, setValue] = useState<string>("");
  const [selectedContact, setSelectedContact] = useState<IContactDetailInfo | null>(null);
  const [editMode, setEditMode] = useState(false);

  const toastRef = useRef<Toast>(null);

  useEffect(() => {
    async function getContactData() {
      useHttpGets(`${ApiEndPoints.tracker}MemberContacts/${memberId}`)
        .then((response) => {
          if (response?.data) {
            setMemberDetails(response.data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
    getContactData();
  }, [memberId, reload, propertyTrackerId]);

  useEffect(() => {
    if (memberDetails?.MemberDetails) {
      const allContactDetails = memberDetails.MemberDetails.flatMap((detail) => detail.Contact) || [];
      const filteredContacts = allContactDetails.filter(
        (contact) =>
          contact.PropertyTrackerId === (propertyTrackerId == 0 ? "" : propertyTrackerId.toString()) ||
          propertyTrackerId === -1
      );
      setContacts(
        filteredContacts.map((contact) => ({
          ...contact,
          Notes:
            memberDetails.MemberDetails.find((detail) => detail.Contact.MemberContactId === contact.MemberContactId)
              ?.Notes || [],
        }))
      );
    }
  }, [memberDetails]);

  const HeaderTemplate = (
    <div className="rounded-t-lg p-2 px-4 bg-slate-600 text-white flex gap-3 justify-around">
      <div className="flex w-1/3 content-start">
        <h3>Contacts</h3>
      </div>
      <div className="flex flex-row gap-3 align-middle w-1/3 content-center"></div>
      <div className="w-1/3 content-center text-end ">
        <REIButton
          id="mpcon-btn-addcontact"
          label="Add a new contact"
          icon="pi pi-plus"
          onClick={() => {
            setEditMode(false);
            setShowDialog(true);
          }}
          size="small"
        ></REIButton>
      </div>
    </div>
  );

  const renderRowToggler = (rowData: any) => {
    const isExpanded = expandedRows?.some((r: any) => r.MemberContactId === rowData.MemberContactId);
    const tooltipText = isExpanded ? "Show less" : "Show more";

    return (
      <>
        <Tooltip target={`.expander-btn-${rowData.MemberContactId}`} content={tooltipText} position="right" />
        <button
          type="button"
          id={`mpcon-btn-toggler-${rowData.MemberContactId}`}
          data-testid={`mpcon-btn-toggler-${rowData.MemberContactId}`}
          className={`p-row-toggler p-link expander-btn-${rowData.MemberContactId}`}
          aria-expanded={isExpanded}
          onClick={() => {
            const newExpanded = isExpanded
              ? expandedRows.filter((r: any) => r.MemberContactId !== rowData.MemberContactId)
              : [...(expandedRows || []), rowData];
            setExpandedRows(newExpanded);
          }}
        >
          <i className={`pi ${isExpanded ? "pi-chevron-down" : "pi-chevron-right"}`} />
        </button>
      </>
    );
  };

  const rowExpansionTemplate = (rowData: IContactDetailInfo) => {
    return (
      <>
        <div className="flex flex-row w-full gap-1 p-1">
          <div className="w-1/6">Contact address:</div>
          <div className="w-5/6">{rowData.Address}</div>
        </div>
        <div className="flex flex-row w-full gap-1 p-1">
          <div className="w-1/6">Website:</div>
          <div className="w-5/6">{rowData.Website}</div>
        </div>
        <REIDivider className="my-1 py-0" />
        <div className="flex flex-row w-full gap-1 p-1">
          <div className="w-1/6">Notes:</div>
          <div className="w-5/6">
            {rowData.Notes.length > 0 &&
              rowData.Notes.map((note) => {
                return (
                  <>
                    <div className="flex flex-row w-full text-sm text-gray-500 items-center">
                      <span>Date: {note.DateCreated.toString()}</span>
                      <REIButton
                        id={`mpcon-btn-deletenote-${note.MemberContactNotedId}`}
                        icon="pi pi-minus-circle"
                        text={true}
                        classNames="p-button-primary"
                        size="small"
                        tooltip="Delete"
                        onClick={(event) => {
                          confirmPopup({
                            target: event.currentTarget,
                            message: Messages.CONFIRMATION_MESSAGE,
                            icon: Icons.INFO_ICON,
                            accept: () => deleteNote(note.MemberContactNotedId),
                            reject: () => {},
                          });
                        }}
                      />
                    </div>
                    <div className="flex flex-row w-full">
                      <div className="flex-0 w-5/6">{note.Note}</div>
                    </div>
                    <REIDivider className="my-1 py-0" />
                  </>
                );
              })}
          </div>
        </div>
        <div className="flex flex-row w-full">
          <div className="w-1/6"></div>
          <div className="w-5/6">
            <REIButton
              id={`mpcon-btn-addnote-${rowData.MemberContactId}`}
              icon=""
              classNames="p-button-primary"
              text={false}
              label="Add a new note"
              onClick={() => {
                setSelectedContact(rowData);
                setShowAddNoteDialog(true);
              }}
            />
          </div>
        </div>
      </>
    );
  };

  const deleteContact = async (contactId: number) => {
    await httpDelete(`${ApiEndPoints.tracker}MemberContacts?memberId=${memberId}&memberContactId=${contactId}`, {})
      .then((result) => {
        if (result.status == 200) {
          setReload(!reload);
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Contact Deleted Successfully",
            life: 2000,
          });
        }
      })
      .catch(() => {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
      });
  };

  const deleteNote = async (noteId: number) => {
    await httpDelete(`${ApiEndPoints.tracker}MemberContactNotes?memberId=${memberId}&memberContactNoteId=${noteId}`, {})
      .then((result) => {
        if (result.status == 200) {
          setReload(!reload);
          const updatedExpandedRows = expandedRows?.filter(
            (row: IContactDetailInfo) => row.MemberContactId !== selectedContact?.MemberContactId
          );
          setExpandedRows(updatedExpandedRows ?? null);
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Note Deleted Successfully",
            life: 2000,
          });
        }
      })
      .catch(() => {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
      });
  };

  const handleDeleteContact = async (event: React.MouseEvent<HTMLElement>, rowData: IContactDetailInfo) => {
    confirmPopup({
      target: event.currentTarget,
      message: Messages.CONFIRMATION_MESSAGE,
      icon: Icons.INFO_ICON,
      accept: () => deleteContact(rowData.MemberContactId),
      reject: () => {},
    });
  };

  const renderActionCell = (rowData: any) => (
    <div className="flex flex-row w-full gap-1 justify-center">
      <REIButton
        id={`mpcon-btn-edit-${rowData.MemberContactId}`}
        icon="pi pi-pencil"
        classNames="mr-1"
        tooltip="Edit contact"
        onClick={(e) => {
          setEditMode(true);
          setSelectedContact(rowData);
          setShowDialog(true);
        }}
      />
      <REIButton
        id={`mpcon-btn-delete-${rowData.MemberContactId}`}
        icon="pi pi-times"
        classNames="p-button-danger"
        tooltip="Delete contact"
        onClick={(e) => {
          handleDeleteContact(e, rowData);
        }}
      />
    </div>
  );

  const addNewNote = async () => {
    const newNote = {
      MemberContactId: selectedContact?.MemberContactId,
      MemberId: memberId,
      Note: value,
    };
    await httpPost(`${ApiEndPoints.tracker}MemberContactNote/Add/`, newNote)
      .then((result) => {
        if (result.status == 200) {
          setShowAddNoteDialog(false);
          setReload(!reload);
          setValue("");
          if (selectedContact) {
            setExpandedRows((prev: any[]) =>
              prev?.filter((row) => row.MemberContactId !== selectedContact.MemberContactId)
            );
          }
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Note Added Successfully",
            life: 2000,
          });
        }
      })
      .catch(() => {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
        setShowAddNoteDialog(false);
      });
  };

  const renderAddNewNote = () => (
    <div className="w-full gap-2 items-center">
      <div className="w-full mb-2">
        <InputTextarea
          id="mpcon-txt-note"
          data-testid="mpcon-txt-note"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={5}
          className={"w-full"}
          placeholder="Enter your note here..."
        />
      </div>
      <div className="w-full justify-end text-end">
        <REIButton label="Cancel" id="mpcon-btn-cancelnote" classNames="mr-1" onClick={() => setShowAddNoteDialog(false)} severity="secondary" />
        <REIButton label="Save" id="mpcon-btn-savenote" type="submit" disabled={!(value && value !== "")} onClick={addNewNote} />
      </div>
    </div>
  );

  return (
    <>
      <ConfirmPopup />
      <Tooltip target=".contact-table .custom-toggler .p-row-toggler" content="Show more" position="right" />

      <REIToastContainer toastReference={toastRef}></REIToastContainer>
      <REIDialoge
        showHeader={true}
        header={`Add a new note`}
        visible={showAddNoteDialog}
        position="center"
        style={{ width: "40vw" }}
        contentComponent={renderAddNewNote()}
        handleEditClick={() => {
          setShowAddNoteDialog(false);
        }}
      ></REIDialoge>
      <REIDialoge
        showHeader={true}
        header={editMode ? `Edit contact` : `Add a new contact to phonebook`}
        visible={showDialog}
        position="center"
        style={{ width: "60vw" }}
        contentComponent={
          <AddContactDetails
            propertyTrackerId={propertyTrackerId}
            editMode={editMode}
            contact={selectedContact}
            closeDialogue={() => {
              setEditMode(false);
              setReload(!reload);
              setShowDialog(false);
              setSelectedContact(null);
            }}
            properties={properties ? properties?.filter((item) => item.value !== -1) : undefined}
          />
        }
        handleEditClick={() => {
          setShowDialog(false);
        }}
      ></REIDialoge>
      <div className="w-full mb-2">
        <REIToggablePanel
          contentComponent={
            contacts.length > 0 ? (
              <div className="altRowShade">
                <DataTable
                  value={contacts}
                  className="mb-4"
                  expandedRows={expandedRows}
                  onRowToggle={(e) => setExpandedRows(e.data)}
                  size="small"
                  rowExpansionTemplate={rowExpansionTemplate}
                >
                  <Column field="ContactFor" header="Contact For"></Column>
                  <Column field="Name" header="Name"></Column>
                  <Column field="Role" header="Role"></Column>
                  <Column field="Company" header="Company"></Column>
                  <Column field="Phone" header="Phone"></Column>
                  <Column field="Mobile" header="Mobile"></Column>
                  <Column field="Email" header="Email"></Column>
                  <Column header="Edit" style={{ width: "7.5rem", textAlign: "left" }} body={renderActionCell} />
                  <Column body={(rowData) => renderRowToggler(rowData)} style={{ width: "3em" }} />
                </DataTable>
              </div>
            ) : (
              <div className="flex content-start">
                <h3>No contact details available.</h3>
              </div>
            )
          }
          defaultCollapse={false}
          header=""
          istoggleable={false}
          customHeader={HeaderTemplate}
        ></REIToggablePanel>
      </div>
    </>
  );
};

export default ContactDetails;
